/**
 * Tenants API Client
 *
 * Create, inspect, and delete customer tenants (ZITADEL orgs) via the
 * tenant-management backend. These operations require the caller to be a staff
 * admin holding instance-level IAM authority (`canManageTenants`); the backend
 * enforces this — the client is a thin transport.
 *
 * Endpoints:
 * - POST   /api/tenant-management/orgs            - Create a tenant
 * - GET    /api/tenant-management/orgs/{orgId}    - Tenant details
 * - DELETE /api/tenant-management/orgs/{orgId}    - Delete a tenant
 *
 * The /api/tenant-management/ service prefix lets the proxy HTTPRoute route
 * these to the tenant-management backend. Requests carry credentials:'include'
 * and the X-MP-Token-Handler CSRF header (added for /api/* paths by http.ts).
 *
 * @module api/TenantsApiClient
 */

import type {
  CreateTenantRequest,
  CreateTenantResponse,
  DeleteTenantResponse,
  TenantDetails,
  UpdateTenantRequest,
  UpdateTenantResponse,
} from '../types';
import { httpGet, httpPost, httpPatch, httpDelete, type HttpOptions } from './http';

// =============================================================================
// Interface
// =============================================================================

/**
 * Interface for tenant lifecycle operations.
 */
export interface TenantsApiClient {
  /** Provision a new customer tenant (org + metadata + first admin). */
  createTenant(req: CreateTenantRequest): Promise<CreateTenantResponse>;

  /** Fetch a tenant's details (metadata, user count, protection flags). */
  getTenant(orgId: string): Promise<TenantDetails>;

  /**
   * Partially update a tenant's editable fields (name, subscription status,
   * entitlements, limits). Only the provided fields change; customerId
   * is immutable and not accepted. Protected orgs (vendor/system/default) are
   * refused with 409 CANNOT_MODIFY_VENDOR / CANNOT_MODIFY_PROTECTED.
   */
  updateTenant(
    orgId: string,
    req: UpdateTenantRequest,
  ): Promise<UpdateTenantResponse>;

  /** Permanently delete a tenant (cascades in ZITADEL). */
  deleteTenant(orgId: string): Promise<DeleteTenantResponse>;
}

// =============================================================================
// Constants
// =============================================================================

const ORGS_ENDPOINT = '/api/tenant-management/orgs';

function tenantPath(orgId: string): string {
  return `${ORGS_ENDPOINT}/${encodeURIComponent(orgId)}`;
}

// =============================================================================
// Real Implementation
// =============================================================================

/**
 * Real implementation of TenantsApiClient that calls the tenant-management
 * backend.
 */
export class RealTenantsApiClient implements TenantsApiClient {
  private readonly options?: HttpOptions;

  constructor(options?: HttpOptions) {
    this.options = options;
  }

  async createTenant(req: CreateTenantRequest): Promise<CreateTenantResponse> {
    return httpPost<CreateTenantResponse>(ORGS_ENDPOINT, req, this.options);
  }

  async getTenant(orgId: string): Promise<TenantDetails> {
    return httpGet<TenantDetails>(tenantPath(orgId), this.options);
  }

  async updateTenant(
    orgId: string,
    req: UpdateTenantRequest,
  ): Promise<UpdateTenantResponse> {
    return httpPatch<UpdateTenantResponse>(tenantPath(orgId), req, this.options);
  }

  async deleteTenant(orgId: string): Promise<DeleteTenantResponse> {
    return httpDelete<DeleteTenantResponse>(tenantPath(orgId), this.options);
  }
}

// =============================================================================
// Default Instance
// =============================================================================

/**
 * Default singleton instance of the Real Tenants API client.
 *
 * Also re-exported from the registry as `tenantsClient` for consistency with
 * the other domains. Unit tests inject a fake client into components/stores
 * directly rather than going through the registry.
 */
export const tenantsApiClient = new RealTenantsApiClient();
